<script setup>
import { onMounted, ref } from 'vue';
import { EditorView } from 'codemirror';
import { EditorState } from '@codemirror/state';
import { html } from '@codemirror/lang-html';
import { css } from '@codemirror/lang-css';
import { lineNumbers, highlightActiveLineGutter, dropCursor, keymap } from '@codemirror/view';
import { search } from '@codemirror/search';
import { autocompletion } from '@codemirror/autocomplete';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { bracketMatching, indentOnInput, syntaxHighlighting, HighlightStyle } from '@codemirror/language';
import { tags as t } from '@lezer/highlight';

const props = defineProps({
  modelValue: {
    type: String,
    required: true,
  },
  language: {
    type: String,
    default: 'html',
    validator: (value) => ['html', 'css'].includes(value),
  },
  theme: {
    type: String,
    default: 'dark',
    validator: (value) => ['light', 'dark'].includes(value),
  },
  height: {
    type: String,
    default: '600px',
  },
  placeholder: {
    type: String,
    default: 'Escreva seu código aqui...',
  },
});

const emit = defineEmits(['update:modelValue']);

const editorContainer = ref(null);
const editor = ref(null);

function getLanguageExtension() {
  return props.language === 'html' ? html() : css();
}

function createEditor() {
  if (!editorContainer.value) return;

  // Definir cores para syntax highlighting usando a API nativa do CodeMirror
  // Usar apenas tags que existem de verdade
  const highlightStyleConfig = [];

  if (t.comment) highlightStyleConfig.push({ tag: t.comment, color: '#6a9955', fontStyle: 'italic' });
  if (t.string) highlightStyleConfig.push({ tag: t.string, color: '#ce9178' });
  if (t.number) highlightStyleConfig.push({ tag: t.number, color: '#b5cea8' });
  if (t.variableName) highlightStyleConfig.push({ tag: t.variableName, color: '#9cdcfe' });
  if (t.tagName) highlightStyleConfig.push({ tag: t.tagName, color: '#569cd6' });
  if (t.attributeName) highlightStyleConfig.push({ tag: t.attributeName, color: '#9cdcfe' });
  if (t.bracket) highlightStyleConfig.push({ tag: t.bracket, color: '#d4d4d4' });
  if (t.punctuation) highlightStyleConfig.push({ tag: t.punctuation, color: '#d4d4d4' });
  if (t.operator) highlightStyleConfig.push({ tag: t.operator, color: '#d4d4d4' });
  if (t.keyword) highlightStyleConfig.push({ tag: t.keyword, color: '#569cd6' });

  const highlightStyle = HighlightStyle.define(highlightStyleConfig);

  // Criar tema personalizado usando a API do CodeMirror
  const customTheme = EditorView.theme(
    {
      '.cm-editor': {
        height: '100%',
        backgroundColor: '#1e1e1e',
        color: '#d4d4d4',
        fontSize: '14px',
        fontFamily: "'Courier New', Courier, monospace",
      },
      '.cm-gutters': {
        backgroundColor: '#252526',
        borderRight: '1px solid #3e3e42',
      },
      '.cm-linenumber': {
        color: '#858585',
      },
      '.cm-activeLineGutter': {
        backgroundColor: '#2d2d30',
      },
      '.cm-content': {
        color: '#d4d4d4',
        padding: '10px 0',
      },
      '.cm-line': {
        color: '#d4d4d4',
      },
      '.cm-cursor': {
        borderLeftColor: '#d4d4d4',
        borderLeft: '2px solid #d4d4d4',
      },
      '.cm-selection': {
        backgroundColor: '#264f78',
      },
      '.cm-selectionBackground': {
        backgroundColor: '#264f78',
      },
      '.cm-tag': {
        color: '#569cd6',
      },
      '.cm-tagName': {
        color: '#569cd6',
        fontWeight: 'bold',
      },
      '.cm-attributeName': {
        color: '#9cdcfe',
      },
      '.cm-string': {
        color: '#ce9178',
      },
      '.cm-comment': {
        color: '#6a9955',
        fontStyle: 'italic',
      },
      '.cm-atom': {
        color: '#b5cea8',
      },
      '.cm-number': {
        color: '#b5cea8',
      },
      '.cm-keyword': {
        color: '#569cd6',
      },
      '.cm-operator': {
        color: '#d4d4d4',
      },
      '.cm-bracket': {
        color: '#d4d4d4',
      },
      '.cm-punctuation': {
        color: '#d4d4d4',
      },
      '.cm-variableName': {
        color: '#9cdcfe',
      },
      '.cm-scroller': {
        overflow: 'auto',
      },
      '.cm-scrollbar': {
        scrollbarWidth: 'thin',
        scrollbarColor: '#464647 #1e1e1e',
      },
      '.cm-scrollbar-vertical': {
        width: '12px',
      },
      '.cm-scrollbar-horizontal': {
        height: '12px',
      },
      '.cm-scrollbar::-webkit-scrollbar': {
        width: '12px',
        height: '12px',
        backgroundColor: '#1e1e1e',
      },
      '.cm-scrollbar::-webkit-scrollbar-track': {
        backgroundColor: '#1e1e1e',
      },
      '.cm-scrollbar::-webkit-scrollbar-thumb': {
        backgroundColor: '#464647',
        borderRadius: '6px',
      },
      '.cm-scrollbar::-webkit-scrollbar-thumb:hover': {
        backgroundColor: '#656566',
      },
    },
    { dark: true },
  );

  const state = EditorState.create({
    doc: props.modelValue,
    extensions: [
      customTheme,
      syntaxHighlighting(highlightStyle),
      lineNumbers(),
      highlightActiveLineGutter(),
      dropCursor(),
      search({ top: true }),
      autocompletion(),
      bracketMatching(),
      indentOnInput(),
      history(),
      keymap.of([
        ...defaultKeymap,
        ...historyKeymap,
        indentWithTab,
      ]),
      getLanguageExtension(),
      EditorView.updateListener.of((update) => {
        if (update.docChanged) {
          emit('update:modelValue', update.state.doc.toString());
        }
      }),
    ],
    tabSize: 2,
  });

  editor.value = new EditorView({
    state,
    parent: editorContainer.value,
  });
}

function updateValue(newValue) {
  if (editor.value && editor.value.state.doc.toString() !== newValue) {
    editor.value.dispatch({
      changes: {
        from: 0,
        to: editor.value.state.doc.length,
        insert: newValue,
      },
    });
  }
}

onMounted(() => {
  createEditor();
});

defineExpose({
  updateValue,
  editor,
  getEditor: () => editor.value,
});
</script>

<template>
  <div class="code-editor-wrapper" :style="{height}">
    <div ref="editorContainer" class="code-editor-container"></div>
  </div>
</template>

<style scoped>
.code-editor-wrapper {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  overflow: hidden;
  background: #1e1e1e;
  position: relative;
  display: flex;
  height: 600px;
}

.code-editor-container {
  height: 100%;
  width: 100%;
  font-family: 'Courier New', Courier, monospace;
  font-size: 14px;
  overflow: auto;
}

/* Estilizar a barra de rolagem nativa */
:deep(.cm-scroller) {
  overflow: auto !important;
}

/* Webkit browsers (Chrome, Safari, Edge) */
:deep(.cm-scroller::-webkit-scrollbar) {
  width: 12px;
  height: 12px;
}

:deep(.cm-scroller::-webkit-scrollbar-track) {
  background: #1e1e1e;
}

:deep(.cm-scroller::-webkit-scrollbar-thumb) {
  background: #464647;
  border-radius: 6px;
}

:deep(.cm-scroller::-webkit-scrollbar-thumb:hover) {
  background: #656566;
}
</style>
